import type { TableColumn, TablePagination } from '@lifebit-ai/lds'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { getDataAccessRequestsTableColumns } from 'modules/requests/data-access-requests/dataAccessRequestsTableColumns'
import { getDataAccessRequestDetailsPath } from 'modules/requests/requestDetailPaths'
import { useGetDataAccessRequestsVerification } from 'services/data-access-requests/hooks/useGetDataAccessRequestsVerification'
import type {
  DataAccessRequestVerificationListItem,
  GetDataAccessRequestsVerificationParams
} from 'services/data-access-requests/types'

export interface DataAccessRequestsTableFiltersState {
  assigneeIds: string[]
  labelIds: string[]
  requesterIds: string[]
  search: string
  status: string[]
  workspaceIds: string[]
}

export const defaultDataAccessRequestsTableFiltersState = (): DataAccessRequestsTableFiltersState => ({
  assigneeIds: [],
  labelIds: [],
  requesterIds: [],
  search: '',
  status: [],
  workspaceIds: []
})

export const buildDataAccessRequestsVerificationParams = (
  tableState: { page: number; pageSize: number },
  filtersState: DataAccessRequestsTableFiltersState
): GetDataAccessRequestsVerificationParams => ({
  page: tableState.page + 1,
  perPage: tableState.pageSize,
  ...(filtersState.search ? { search: filtersState.search } : {}),
  ...(filtersState.status.length ? { status: filtersState.status.join(',') } : {}),
  ...(filtersState.workspaceIds.length ? { workspaceIds: filtersState.workspaceIds.join(',') } : {}),
  ...(filtersState.requesterIds.length ? { requesterIds: filtersState.requesterIds.join(',') } : {}),
  ...(filtersState.assigneeIds.length ? { assigneeIds: filtersState.assigneeIds.join(',') } : {}),
  ...(filtersState.labelIds.length ? { labelIds: filtersState.labelIds.join(',') } : {})
})

export const useDataAccessRequestsTable = (
  queryParams: GetDataAccessRequestsVerificationParams
): {
  columns: TableColumn<DataAccessRequestVerificationListItem>[]
  dataAccessRequests: DataAccessRequestVerificationListItem[]
  isLoading: boolean
  openDataAccessRequest: (requestId: string) => void
  pagination: TablePagination
  showPagination: boolean
} => {
  const navigate = useNavigate()

  const { data: paginatedResponse, isLoading } = useGetDataAccessRequestsVerification(queryParams)

  const columns = useMemo(() => getDataAccessRequestsTableColumns(), [])

  const dataAccessRequests = paginatedResponse?.data ?? []

  const pagination: TablePagination = useMemo(
    () => ({
      pageSize: queryParams.perPage,
      currentPage: queryParams.page - 1,
      totalRows: paginatedResponse?.pagination?.totalCount || 0,
      pageSizeOptions: [8, 10, 20, 50],
      onPageChange: () => undefined,
      onPageSizeChange: () => undefined
    }),
    [paginatedResponse?.pagination?.totalCount, queryParams.page, queryParams.perPage]
  )

  const openDataAccessRequest = useCallback(
    (requestId: string): void => {
      if (!requestId) return

      navigate(getDataAccessRequestDetailsPath(requestId))
    },
    [navigate]
  )

  return {
    columns,
    dataAccessRequests,
    isLoading,
    openDataAccessRequest,
    pagination,
    showPagination: dataAccessRequests.length > 0
  }
}
